/**
 * Content schemas and the loader.
 *
 * Game content lives in JSON so balance is a text edit rather than a code change.
 * The price of that is that a typo in a data file becomes a runtime bug — so every
 * file is validated on load, and the game refuses to start on bad content rather
 * than limping along with an undefined terrain type.
 *
 * The loader also assigns each entry a *dense integer id* (its position in the file)
 * because those ids are what the typed-array tile planes store. The string key is
 * kept alongside, and written into every save, so that reordering a content file
 * remaps old saves instead of corrupting them.
 */

import { z } from 'zod';

import terrainData from '../terrain.json' with { type: 'json' };
import featureData from '../features.json' with { type: 'json' };
import resourceData from '../resources.json' with { type: 'json' };
import improvementData from '../improvements.json' with { type: 'json' };
import unitData from '../units.json' with { type: 'json' };
import rulesData from '../rules.json' with { type: 'json' };
import civilizationData from '../civilizations.json' with { type: 'json' };
import buildingData from '../buildings.json' with { type: 'json' };
import mapgenData from '../mapgen-presets.json' with { type: 'json' };
import eraData from '../eras.json' with { type: 'json' };
import technologyData from '../technologies.json' with { type: 'json' };
import specialistData from '../specialists.json' with { type: 'json' };
import personalityData from '../personalities.json' with { type: 'json' };
import governmentData from '../governments.json' with { type: 'json' };
import traitData from '../traits.json' with { type: 'json' };

// ─── shared shapes ─────────────────────────────────────────────────────────────

const Yields = z.object({
  food: z.number(),
  production: z.number(),
  gold: z.number(),
});
export type Yields = z.infer<typeof Yields>;

const key = z
  .string()
  .min(2)
  .regex(/^[a-z][a-z0-9_]*$/, 'keys must be lower_snake_case');

const colour = z.string().regex(/^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/, 'colour must be #RRGGBB or #RRGGBBAA');

// ─── per-file schemas ──────────────────────────────────────────────────────────

const TerrainDef = z.object({
  key,
  name: z.string().min(1),
  water: z.boolean(),
  passable: z.boolean(),
  /** Open ocean, closed to coastal ships. */
  deep: z.boolean().default(false),
  yields: Yields,
  movementCost: z.number().int().positive(),
  defenceBonus: z.number().int().min(0),
  colour,
  description: z.string().min(1),
});
export type TerrainDef = z.infer<typeof TerrainDef> & { id: number };

const FeatureDef = z.object({
  key,
  name: z.string().min(1),
  yields: Yields,
  movementCost: z.number().int().min(0),
  defenceBonus: z.number().int().min(0),
  validTerrain: z.array(z.string()),
  colour,
  description: z.string().min(1),
});
export type FeatureDef = z.infer<typeof FeatureDef> & { id: number };

/**
 * The pictures a resource can be drawn with. Content picks one by name; the renderer
 * supplies the drawing. Several resources may share a picture — copper, iron and coal
 * are all ore in rock, told apart by the colour of the ore.
 *
 * The list lives here rather than in the renderer so that content is validated against
 * it at load time, and the renderer's table of drawings is typed from it — so adding a
 * name here without drawing it is a compile error, not a blank tile.
 */
export const RESOURCE_ICONS = [
  'none',
  'wheat',
  'fish',
  'cattle',
  'horse',
  'deer',
  'ore',
  'crystals',
  'gem',
  'spice',
  'silk',
  'incense',
  'pearl',
  'stone',
  'oil',
] as const;
export type ResourceIcon = (typeof RESOURCE_ICONS)[number];

const ResourceDef = z.object({
  key,
  name: z.string().min(1),
  class: z.enum(['strategic', 'luxury', 'bonus']),
  yields: Yields,
  validTerrain: z.array(z.string()),
  rarity: z.number().min(0),
  colour,
  icon: z.enum(RESOURCE_ICONS),
  /**
   * The technology that reveals it (ADR-0063): until then its owner sees no icon and
   * gets no yield from it. Null: always seen.
   */
  revealedBy: z.string().nullable().default(null),
  description: z.string().min(1),
});
export type ResourceDef = z.infer<typeof ResourceDef> & { id: number; revealedId: number };

/**
 * How an improvement is drawn. Same arrangement as RESOURCE_ICONS: content picks a name,
 * the renderer's table of drawings is typed from this list, and a name with no drawing
 * does not compile. `road` is special — it is drawn as a network joining its neighbours
 * rather than as a picture on its own tile.
 */
export const IMPROVEMENT_ARTS = ['none', 'fields', 'orchard', 'plantation', 'mine', 'quarry', 'road'] as const;
export type ImprovementArt = (typeof IMPROVEMENT_ARTS)[number];

const ImprovementDef = z.object({
  key,
  name: z.string().min(1),
  art: z.enum(IMPROVEMENT_ARTS),
  /**
   * A route — a road — lies in its own layer beside whatever else is built on the tile,
   * rather than replacing it (ADR-0055).
   */
  route: z.boolean().default(false),
  /** The technology a worker must know of before building it. Null: from the start. */
  requiresTech: z.string().nullable().default(null),
  yields: Yields,
  validTerrain: z.array(z.string()),
  turnsToBuild: z.number().int().min(0),
  effects: z
    .object({
      /** Share of this city's administrative drag the improvement recovers. */
      dragReduction: z.number().min(0).max(1).optional(),
      /** Part of the road network. Trade between connected cities is a later phase. */
      connectsCities: z.boolean().optional(),
    })
    .strict()
    .default({}),
  description: z.string().min(1),
});
export type ImprovementDef = z.infer<typeof ImprovementDef> & { id: number; techId: number };

/**
 * A unit type.
 *
 * `abilities` is a closed enum rather than free strings: a typo in a data file
 * should fail at load, and every ability listed here has code behind it. Adding an
 * ability means adding it in both places, which is the point.
 */
const UnitDef = z.object({
  key,
  name: z.string().min(1),
  domain: z.enum(['land', 'sea', 'air']),
  /** Which shape the renderer draws. Real unit art arrives in Phase 14. */
  glyph: z.enum(['chevron', 'shield', 'disc', 'ship', 'siege', 'plane']),
  attack: z.number().int().min(0),
  defence: z.number().int().min(0),
  hp: z.number().int().positive(),
  /** Whole movement points. Multiplied by rules.movement.scale to get move points. */
  movement: z.number().int().positive(),
  /** Sight radius in hexes. */
  sight: z.number().int().min(1),
  cost: z.number().int().min(0),
  upkeep: z.number().int().min(0),
  /** The technology needed before any city can build it. Null: from the start. */
  requiresTech: z.string().nullable().default(null),
  /** Once this technology is known the unit is no longer offered — something better is. */
  obsoleteWith: z.string().nullable().default(null),
  /** siege: city defences do not count against its attacks (ADR-0064). */
  abilities: z.array(z.enum(['ignore_terrain_cost', 'found_city', 'build_improvement', 'ignore_zoc', 'siege', 'intercept'])),
  /** An aircraft's reach from its base city, in tiles (ADR-0065). Set exactly for air units. */
  airRange: z.number().int().min(1).max(20).nullable().default(null),
  /**
   * Fire at an enemy within `range` without closing in (ADR-0064): `shots` rolls of
   * strength against the defence, each hit doing combat.damagePerRound, never killing.
   */
  bombard: z
    .object({ strength: z.number().int().positive(), range: z.number().int().min(1).max(3), shots: z.number().int().min(1).max(10) })
    .nullable()
    .default(null),
  /** What this unit upgrades to (ADR-0050). Null: nothing. */
  upgradeTo: z.string().nullable().default(null),
  /** Strategic resources a city must have to build it (ADR-0063). */
  requiresResources: z.array(z.string()).default([]),
  /** Land units a ship carries. Zero for everything that is not a ship that carries. */
  capacity: z.number().int().min(0).default(0),
  /** Aircraft a ship can base (ADR-0066): an aircraft carrier. */
  airCapacity: z.number().int().min(0).default(0),
  /** A ship that cannot enter deep water. */
  coastal: z.boolean().default(false),
  /** Citizens spent to build this unit. A settler is people, not just equipment. */
  populationCost: z.number().int().min(0).default(0),
  /** A unique unit: the one civilisation (civilizations.json key) that may build it (ADR-0062). */
  civilization: z.string().nullable().default(null),
  /** The standard unit it takes the place of, for that civilisation. */
  replaces: z.string().nullable().default(null),
  description: z.string().min(1),
});
export type UnitDef = z.infer<typeof UnitDef> & {
  id: number;
  techId: number;
  obsoleteTechId: number;
  upgradeId: number;
  /** The civilisation id a unique unit belongs to, or -1. */
  civId: number;
  /** The unit a unique unit replaces, or -1. */
  replacesId: number;
  /** requiresResources as resource ids. */
  resourceIds: number[];
};

/**
 * A building a city can produce.
 *
 * `effects` carries only what the engine actually applies. Upkeep, defence bonuses
 * and happiness all belong to later phases and are deliberately absent — a data field
 * with no code behind it reads as a feature and behaves as a lie.
 */
const BuildingDef = z.object({
  key,
  name: z.string().min(1),
  cost: z.number().int().min(0),
  /** Gold per turn to keep it standing. Over-build and the treasury empties. */
  upkeep: z.number().int().min(0).default(0),
  /** The technology needed before any city can build it. Null: from the start. */
  requiresTech: z.string().nullable().default(null),
  effects: z
    .object({
      /** Share of the food box kept after growing, rather than starting again at nothing. */
      growthFoodKept: z.number().min(0).max(1).optional(),
      productionBonus: z.number().int().min(0).optional(),
      goldBonus: z.number().int().min(0).optional(),
      sizeCapIncrease: z.number().int().min(0).optional(),
      /** Citizens this building keeps content. */
      contentment: z.number().int().min(0).optional(),
      /** Share of administrative drag this building recovers. */
      dragReduction: z.number().min(0).max(1).optional(),
      /** Marks the city as the seat of government. Exactly one per civilisation. */
      isSeatOfGovernment: z.boolean().optional(),
      /** Percent added to the city's science. Several add together: a library and a university make +100%. */
      sciencePercent: z.number().int().min(0).max(500).optional(),
      /** Units trained here start one rank up. */
      veteranUnits: z.boolean().optional(),
      /** Culture points a turn, toward the city's borders and its civilisation's prestige (ADR-0057). */
      culture: z.number().int().min(0).optional(),
      /** Percent added to defenders in the city against all but siege units (ADR-0064). */
      wallsDefence: z.number().int().min(0).optional(),
      /** Percent chance each anti-aircraft shot hits a striking aircraft (ADR-0065). */
      antiAir: z.number().int().min(0).max(100).optional(),
    })
    .strict(),
  /** A world wonder: built once in the whole world (ADR-0057). */
  wonder: z.boolean().default(false),
  /**
   * A wonder's effects on every city its holder owns, its own included. Only the effects
   * the engine applies civilisation-wide are allowed here.
   */
  civEffects: z
    .object({
      contentment: z.number().int().min(0).optional(),
      productionBonus: z.number().int().min(0).optional(),
      goldBonus: z.number().int().min(0).optional(),
      sciencePercent: z.number().int().min(0).max(500).optional(),
      culture: z.number().int().min(0).optional(),
      veteranUnits: z.boolean().optional(),
    })
    .strict()
    .default({}),
  description: z.string().min(1),
}).refine((b) => b.wonder || Object.keys(b.civEffects).length === 0, {
  message: 'only a wonder may have civEffects',
});
export type BuildingDef = z.infer<typeof BuildingDef> & { id: number; techId: number };

/**
 * A playable civilisation.
 *
 * City names are real toponyms for the culture, and the leader is a well-attested
 * figure chosen away from the genre's default — both per ADR-0008. Phase 12 expands
 * this file to a full roster; one entry is enough to prove the wiring.
 */
const CivilizationDef = z.object({
  key,
  name: z.string().min(1),
  adjective: z.string().min(1),
  leader: z.string().min(1),
  colour,
  cityNames: z.array(z.string().min(1)).min(4),
  /** What the civilisation knows on turn one. Roots, or with their prerequisites also listed. */
  startingTechs: z.array(z.string()).default([]),
  /** The AI personality it leans to, from personalities.json. */
  personality: z.string(),
  /** Two traits from traits.json (ADR-0062). */
  traits: z.array(z.string()).length(2),
  description: z.string().min(1),
});
export type CivilizationDef = z.infer<typeof CivilizationDef> & {
  id: number;
  startingTechIds: number[];
  personalityId: number;
  traitIds: number[];
  /** Its unique unit's id, or -1. */
  uniqueUnitId: number;
};

/** A civilisation trait (ADR-0062). Every effect has code behind it. */
const TraitDef = z.object({
  key,
  name: z.string().min(1),
  summary: z.string().min(1),
  effects: z
    .object({
      buildingDiscount: z.record(z.string(), z.number().int().min(1).max(90)).default({}),
      unitDiscount: z.record(z.string(), z.number().int().min(1).max(90)).default({}),
      sciencePercent: z.number().int().min(0).default(0),
      goldPerCity: z.number().int().min(0).default(0),
      centreFood: z.number().int().min(0).default(0),
      promotionPercent: z.number().int().min(0).default(0),
      seaMovement: z.number().int().min(0).default(0),
      anarchyTurnsLess: z.number().int().min(0).default(0),
    })
    .strict(),
});
export type TraitDef = z.infer<typeof TraitDef> & { id: number };

/** An AI personality: multipliers on `rules.ai` weights (ADR-0053). */
const PersonalityDef = z.object({
  key,
  name: z.string().min(1),
  /** One line for the Rivals screen. */
  summary: z.string().min(1),
  weights: z.record(z.string(), z.number().positive()),
});
export type PersonalityDef = z.infer<typeof PersonalityDef> & { id: number };

/** A form of government (ADR-0059). Every effect has code behind it; see governments.json. */
const GovernmentDef = z.object({
  key,
  name: z.string().min(1),
  requiresTech: z.string().nullable().default(null),
  /** False for anarchy: a state a civilisation passes through, never one it chooses. */
  selectable: z.boolean(),
  summary: z.string().min(1),
  effects: z
    .object({
      maxScienceRate: z.number().int().min(0).max(100),
      dragMultiplier: z.number().min(0),
      dragFlat: z.number().min(0).max(1),
      tilePenalty: z.boolean(),
      goldTileBonus: z.number().int().min(0),
      goldTileMinimum: z.number().int().min(1),
      freeUnitsPerCity: z.number().int().min(0),
      contentment: z.number().int().min(0),
      garrisonContentment: z.number().int().min(0),
      maxGarrisonContentment: z.number().int().min(0),
      warWeariness: z.number().min(0),
      productionPercent: z.number().int().min(-100),
      culturePercent: z.number().int().min(-100),
    })
    .strict(),
  aiValue: z.record(z.string(), z.number()),
});
export type GovernmentDef = z.infer<typeof GovernmentDef> & { id: number; techId: number };

/** Global rules that are not attached to any one piece of content. */
/**
 * A job for a citizen who works no tile. Every field of `yields` has code behind it:
 * science joins the city's own science (and is raised by its libraries), gold goes
 * straight to the treasury, and contentment keeps that many citizens content.
 */
const SpecialistDef = z.object({
  key,
  name: z.string().min(1),
  plural: z.string().min(1),
  yields: z
    .object({
      science: z.number().int().min(0),
      gold: z.number().int().min(0),
      contentment: z.number().int().min(0),
    })
    .strict(),
  description: z.string().min(1),
});
export type SpecialistDef = z.infer<typeof SpecialistDef> & { id: number };

/** An age of history. Order in the file is the order they come in. */
const EraDef = z.object({
  key,
  name: z.string().min(1),
  description: z.string().min(1),
});
export type EraDef = z.infer<typeof EraDef> & { id: number };

/**
 * A technology.
 *
 * Deliberately thin. There is no cost here — cost comes from the technology's depth in
 * the tree and two numbers in rules.research, so the pace of the whole game is tuned in
 * one place rather than eighty. And there is no list of what it unlocks: that is
 * declared on the unit, building or improvement that needs it (Phase 6b), so adding a
 * new unit never means editing the tree.
 */
const TechDef = z.object({
  key,
  name: z.string().min(1),
  era: z.string(),
  /** Every one of these must be known before this can be researched. */
  requires: z.array(z.string()),
  description: z.string().min(1),
});
export type TechnologyDef = z.infer<typeof TechDef> & {
  id: number;
  /** Position of its era in eras.json. */
  eraIndex: number;
  /** `requires`, as technology ids. Resolved once at load so the engine never looks up a key. */
  prerequisites: number[];
  /** Longest chain of prerequisites beneath it. Roots are 0. Drives cost and the tree's layout. */
  depth: number;
};

const Rules = z.object({
  movement: z.object({
    /** Move points per whole movement point. 3 means a unit's moves are counted in thirds. */
    scale: z.number().int().min(1).max(12),
    allowSingleTileWhenDepleted: z.boolean(),
    /** Move points to step from a road tile to another road tile. */
    roadCost: z.number().int().min(1),
  }),
  vision: z.object({
    elevationBonus: z.number().int().min(0),
    cityRadius: z.number().int().min(0),
    highGroundTerrain: z.array(z.string()),
  }),
  start: z.object({
    ringRadius: z.number().int().min(1).max(5),
    polarMargin: z.number().min(0).max(1),
    minSeparation: z.number().int().min(0),
    foodWeight: z.number(),
    productionWeight: z.number(),
    goldWeight: z.number(),
    resourceBonus: z.number(),
    freshWaterBonus: z.number(),
    coastBonus: z.number(),
    waterTilePenalty: z.number(),
  }),
  cities: z.object({
    startingPopulation: z.number().int().min(1),
    foodPerCitizen: z.number().int().min(0),
    growthBase: z.number().int().min(1),
    growthPerPop: z.number().int().min(0),
    workRadius: z.number().int().min(1).max(5),
    startingBorderRadius: z.number().int().min(0).max(5),
    minSpacing: z.number().int().min(1),
    centreMinFood: z.number().int().min(0),
    centreMinProduction: z.number().int().min(0),
    centreMinGold: z.number().int().min(0),
    riverGoldBonus: z.number().int().min(0),
    workWeights: z.object({ food: z.number(), production: z.number(), gold: z.number() }),
    capFoodWeight: z.number().min(0),
    baseSizeCap: z.number().int().min(1),
    switchPenalty: z.number().min(0).max(1),
    /** What spare citizens do in a city left on Auto, once disorder is taken care of. */
    autoSpecialist: z.string(),
  }),
  economy: z.object({
    startingTreasury: z.number().int().min(0),
    sellBuildingRefund: z.number().min(0).max(1),
    /** Units' upkeep each city carries for free. */
  }),
  research: z.object({
    /** Cost of a technology with no prerequisites. */
    baseCost: z.number().positive(),
    /** Each step deeper in the tree multiplies the cost by this. */
    lateDepth: z.number().int().min(0),
    lateGrowth: z.number().min(1),
    costGrowth: z.number().min(1),
    /** Share of commerce sent to research in a new game, 0-100. */
    startingRate: z.number().int().min(0).max(100),
    /** The slider moves in steps of this many percent. */
    rateStep: z.number().int().min(1).max(100),
    /** The highest share any civilisation may set. Governments will lower it (Phase 10). */
    maxRate: z.number().int().min(0).max(100),
    /** A city's own scholars: one science per this many citizens, rounded up, before any commerce. */
    citizensPerScience: z.number().int().min(1),
  }),
  drag: z.object({
    lossPerTile: z.number().min(0).max(1),
    maxLoss: z.number().min(0).max(1),
    capitalFreeRadius: z.number().int().min(0),
  }),
  work: z.object({
    maxWorkersPerTile: z.number().int().min(1),
  }),
  ai: z.object({
    settleSearchRadius: z.number().int().min(1),
    settleDistancePenalty: z.number(),
    exploreSearchRadius: z.number().int().min(1),
    exploreDistancePenalty: z.number(),
    settlerValue: z.number(),
    settlerPerCityPenalty: z.number(),
    settlerPerPendingPenalty: z.number(),
    workerValue: z.number(),
    workerPerWorkerPenalty: z.number(),
    defenderValue: z.number(),
    defenderPerUnitPenalty: z.number(),
    scoutValue: z.number(),
    buildingBase: z.number(),
    buildingGrowthWeight: z.number(),
    buildingProductionWeight: z.number(),
    buildingGoldWeight: z.number(),
    buildingContentmentWeight: z.number(),
    buildingSizeCapWeight: z.number(),
    buildingDragWeight: z.number(),
    buildingScienceWeight: z.number(),
    /** How much each thing a technology unlocks makes it worth researching, against its cost. */
    techUnlockWeight: z.number().min(0),
    costPenalty: z.number(),
    personalityTwist: z.number().min(0).max(1),
    techMilitaryWeight: z.number().min(0),
    techScienceWeight: z.number().min(0),
    techGoldWeight: z.number().min(0),
    techGrowthWeight: z.number().min(0),
    governmentSwitchMargin: z.number().min(0),
    governmentWarPenalty: z.number().min(0),
    governmentMinTurns: z.number().int().min(0),
    techGovernmentWeight: z.number().min(0),
    workerPerCityBonus: z.number(),
    roadValue: z.number().min(0),
    resourceRoadValue: z.number().min(0),
    resourceBuyPerTurn: z.number().int().min(0),
    threatRadius: z.number().int().min(1),
    threatPeaceShare: z.number().min(0).max(1),
    threatPerDefender: z.number().positive(),
    maxGarrison: z.number().int().min(1),
    unitStrengthWeight: z.number(),
    warUnitBonus: z.number(),
    unguardedBonus: z.number(),
    garrisonPeace: z.number().int().min(0),
    garrisonWar: z.number().int().min(0),
    warStrengthRatio: z.number().min(1),
    warMinimumUnits: z.number().int().min(0),
    warEarliestTurn: z.number().int().min(0),
    warChancePerTurn: z.number().min(0).max(1),
    warPrepBonus: z.number().min(0),
    treatyWarFactor: z.number().min(0).max(1),
    wonderPerHeldPenalty: z.number().min(0),
    techSalePrice: z.number().min(0),
    gptIncomeShare: z.number().min(0).max(1),
    wonderCostPenalty: z.number().min(0),
    wonderBase: z.number(),
    warPrepLead: z.number().int().min(0),
    attackMinimumOdds: z.number().min(0).max(1),
    siegeUnits: z.number().int().min(1),
    siegeOdds: z.number().min(0).max(1),
    minimumWarTurns: z.number().int().min(0),
    peaceWhenStrengthBelow: z.number().min(0),
    peaceLossRatio: z.number().min(0),
    peaceLossFloor: z.number().min(0),
    peaceWearyCitizens: z.number().int().min(1),
    stalemateTurns: z.number().int().min(1),
    warAims: z.number().int().min(1),
    aimCapitalPenalty: z.number().min(0),
    armyMinimum: z.number().int().min(1),
    armyPerDefender: z.number().min(0),
    armyGuessDefenders: z.number().min(0),
    gatherRadius: z.number().int().min(0),
    gatherMaxTurns: z.number().int().min(1),
    gatherShare: z.number().min(0).max(1),
    siegeArmyShare: z.number().min(0).max(1),
    armyNeedBonus: z.number().min(0),
    marchResolve: z.number().min(0).max(1),
    seaExploreReach: z.number().min(1),
    seaExploreRetry: z.number().int().min(1),
    regroupShare: z.number().min(0).max(1),
    marchSpread: z.number().int().min(0),
    cityKeepFactor: z.number().min(1),
    peaceGoldShare: z.number().min(0).max(1),
    peacePriceShare: z.number().min(0),
    peaceBasePrice: z.number().min(0),
    peaceTradeShare: z.number().min(0).max(1),
    offerChance: z.number().min(0).max(1),
    demandChance: z.number().min(0).max(1),
    demandStrengthRatio: z.number().min(1),
    demandGoldShare: z.number().min(0).max(1),
    yieldStrengthRatio: z.number().min(1),
    openBordersValue: z.number(),
    allianceValue: z.number(),
    dangerTechFactor: z.number().min(1),
    seaScoutValue: z.number(),
    transportValue: z.number(),
    upgradeReserve: z.number().int().min(0),
    upgradeSavingSteps: z.number().int().min(0),
    scoutPerScoutPenalty: z.number(),
    buildingVeteranWeight: z.number(),
    treasuryRunway: z.number().int().min(1),
    siegeShare: z.number().min(0).max(1),
    siegePeacePenalty: z.number().min(0),
    buildingWallsWeight: z.number().min(0),
    wallsPeaceShare: z.number().min(0).max(1),
    airShare: z.number().min(0).max(1),
    airValue: z.number(),
    airPerUnitPenalty: z.number().min(0),
    interceptorBonus: z.number().min(0),
    carrierValue: z.number(),
    carrierStandoff: z.number().int().min(2),
    warshipValue: z.number(),
    homeFleet: z.number().int().min(0),
    huntRadius: z.number().int().min(1),
    airPeacePenalty: z.number().min(0),
    buildingAntiAirWeight: z.number().min(0),
    buildingCultureWeight: z.number(),
    wonderCivShare: z.number().min(0),
  }),
  combat: z.object({
    damagePerRound: z.number().int().min(1),
    fortifyBonus: z.number().int().min(0),
    riverBonus: z.number().int().min(0),
    cityBonus: z.number().int().min(0),
    largeCitySize: z.number().int().min(1),
    largeCityBonus: z.number().int().min(0),
    attackCost: z.number().int().min(1),
    healField: z.number().int().min(0),
    healOwnTerritory: z.number().int().min(0),
    healCity: z.number().int().min(0),
    besiegeTiles: z.number().int().min(1).max(6),
    captureCitizensLost: z.number().int().min(0),
    buildingLossChance: z.number().min(0).max(1),
    ranks: z.array(z.object({ name: z.string().min(1), hpBonus: z.number().int().min(0) })).min(1),
    promotionChance: z.array(z.number().min(0).max(1)),
    upgradeBase: z.number().int().min(0),
    upgradePerShield: z.number().int().min(0),
    bombardMinHp: z.number().int().min(1),
    interceptRounds: z.number().int().min(1),
    antiAirShots: z.number().int().min(0),
  }),
  diplomacy: z.object({
    truceTurns: z.number().int().min(0),
    peaceOfferCooldown: z.number().int().min(0),
    cityLossValue: z.number().min(0),
    cityValuePerCitizen: z.number().min(0),
    cityBuildingShare: z.number().min(0),
    treatyTurns: z.number().int().min(0),
    paymentTurns: z.number().int().min(1),
    offerTurns: z.number().int().min(1),
    aiOfferCooldown: z.number().int().min(0),
    resourceValuePerTurn: z.number().min(0),
    resourceIdlePerTurn: z.number().min(0),
    resourceCostPerTurn: z.number().min(0),
    memories: z.record(
      z.string(),
      z.object({
        value: z.number(),
        turns: z.number().int().min(1),
        max: z.number().positive().optional(),
        label: z.string().min(1),
      }),
    ),
    attitude: z.object({
      atWar: z.number(),
      sharedEnemy: z.number(),
      sameGovernment: z.number(),
      openBorders: z.number(),
      alliance: z.number(),
      borderPressurePerTile: z.number(),
      borderPressureMax: z.number(),
      reputationWeight: z.number(),
      bands: z
        .array(
          z.object({
            name: z.string().min(1),
            below: z.number().nullable(),
            dealMargin: z.number().positive().nullable(),
            warFactor: z.number().min(0),
          }),
        )
        .min(1),
    }),
    reputation: z.object({
      brokeTreaty: z.number().max(0),
      defaulted: z.number().max(0),
      recoveryPerTurn: z.number().min(0),
    }),
  }),
  contentment: z.object({
    contentBase: z.number().int().min(0),
    disorderProductionMultiplier: z.number().min(0).max(1),
  }),
  government: z.object({
    start: z.string(),
    anarchy: z.string(),
    anarchyTurns: z.number().int().min(0),
  }),
  weariness: z.object({
    shieldsPerCitizen: z.number().positive(),
    maxCitizens: z.number().int().min(0),
    decayAtWar: z.number().min(0).max(1),
    decayAtPeace: z.number().min(0).max(1),
  }),
  culture: z.object({
    /** Culture totals at which a city's border grows by one ring, ascending. */
    borderThresholds: z.array(z.number().int().min(1)).refine((t) => t.every((v, i) => i === 0 || v > t[i - 1]!), {
      message: 'borderThresholds must ascend',
    }),
  }),
  score: z.object({
    perCitizen: z.number().min(0),
    perTile: z.number().min(0),
    perTech: z.number().min(0),
    perWonder: z.number().min(0),
    perCulture: z.number().min(0),
    victory: z.number().min(0),
    earlyWinReference: z.number().int().min(0),
    earlyWinPerTurn: z.number().min(0),
    historyEvery: z.number().int().min(1),
  }),
  victory: z.object({
    /** Only conditions the engine knows how to check; a typo is refused at load. */
    conditions: z.array(z.enum(['conquest'])),
  }),
  resources: z.object({
    /** Once known, a civilisation's coastal cities share resources by sea. */
    seaTech: z.string(),
  }),
  autosave: z.object({
    enabled: z.boolean(),
    slots: z.number().int().min(1).max(10),
  }),
});
export type Rules = z.infer<typeof Rules>;

const MapSize = z.object({
  key,
  name: z.string().min(1),
  width: z.number().int().min(8),
  height: z.number().int().min(8),
  suggestedCivs: z.number().int().min(2),
});
export type MapSize = z.infer<typeof MapSize>;

const unit = z.number().min(0).max(1);

/**
 * Map generation parameters.
 *
 * JSON has no comments, so keys beginning with an underscore are treated as notes
 * for whoever is editing the file — `z.object` strips unknown keys, so they are
 * documentation that costs nothing. Every real key is required, which means a typo
 * ("seaLevl") surfaces as a missing-field error rather than being silently ignored.
 */
const MapgenDefaults = z.object({
  size: z.string(),
  wrapX: z.boolean(),

  // Land shape
  seaLevel: unit,
  continentScale: z.number().positive(),
  warpStrength: z.number().min(0),
  octaves: z.number().int().min(1).max(10),
  gain: z.number().min(0.1).max(0.9),
  polarRows: z.number().int().min(0),
  minIslandSize: z.number().int().min(1),

  // Tectonics
  tilesPerPlate: z.number().int().min(20).max(2000),
  minPlates: z.number().int().min(3).max(64),
  oceanicPlateFraction: unit,
  plateSpread: unit,
  plateInfluence: unit,
  boundaryUplift: z.number().min(0),
  riftDepth: z.number().min(0),
  boundaryWidth: z.number().positive(),
  plateWarp: unit,

  // Relief
  mountainFraction: unit,
  hillFraction: unit,

  // Water bodies
  maxLakeSize: z.number().int().min(0),

  // Climate
  temperatureNoise: unit,
  moisturePasses: z.number().int().min(1).max(10),
  evaporationRate: z.number().positive(),
  rainfallRate: z.number().positive(),
  orographicRate: z.number().min(0),
  desertMoisture: unit,
  desertTemperature: unit,
  grasslandMoisture: unit,
  tundraTemperature: unit,
  rainforestTemperature: unit,

  // Features
  forestDensity: unit,
  rainforestDensity: unit,
  marshDensity: unit,
  oasisChance: unit,

  // Rivers
  riverDensity: unit,
  riverMinLength: z.number().int().min(1),

  // Resources
  resourceDensity: unit,
  resourceMinSpacing: z.number().int().min(0),
  minStrategicPerType: z.number().int().min(0),
});
export type MapgenDefaults = z.infer<typeof MapgenDefaults>;

const MapType = z.object({
  key,
  name: z.string().min(1),
  summary: z.string().min(1),
  /** Generation parameters this kind of world changes from the defaults. */
  overrides: z.record(z.string(), z.number()),
});

const MapgenPresets = z.object({
  sizes: z.array(MapSize).min(1),
  defaults: MapgenDefaults,
  /** Kinds of world offered at the new-game screen (ADR-0062). */
  mapTypes: z.array(MapType).min(1),
}).superRefine((presets, ctx) => {
  // A world type may only change numeric generation parameters that exist.
  for (const type of presets.mapTypes) {
    for (const name of Object.keys(type.overrides)) {
      if (typeof (presets.defaults as Record<string, unknown>)[name] !== 'number') {
        ctx.addIssue({ code: 'custom', message: `map type "${type.key}" overrides unknown parameter "${name}"` });
      }
    }
  }
});
export type MapgenPresets = z.infer<typeof MapgenPresets>;

// ─── the registry ──────────────────────────────────────────────────────────────

/** A content table: dense array indexed by id, plus a key → id lookup. */
export interface Table<T extends { key: string; id: number }> {
  all: readonly T[];
  byKey: ReadonlyMap<string, T>;
  /** Throws on an unknown key — content bugs should be loud, not silent. */
  id(key: string): number;
  get(id: number): T;
}

function buildTable<T extends { key: string }>(rows: readonly T[], label: string): Table<T & { id: number }> {
  const all = rows.map((row, id) => ({ ...row, id }));
  const byKey = new Map(all.map((row) => [row.key, row]));
  if (byKey.size !== all.length) {
    const seen = new Set<string>();
    const dupe = all.find((r) => (seen.has(r.key) ? true : (seen.add(r.key), false)));
    throw new Error(`Duplicate key "${dupe?.key}" in ${label}`);
  }
  return {
    all,
    byKey,
    id(k: string): number {
      const row = byKey.get(k);
      if (!row) throw new Error(`Unknown ${label} key: "${k}"`);
      return row.id;
    },
    get(id: number): T & { id: number } {
      const row = all[id];
      if (!row) throw new Error(`Unknown ${label} id: ${id}`);
      return row;
    },
  };
}

export interface Content {
  terrain: Table<TerrainDef>;
  features: Table<FeatureDef>;
  resources: Table<ResourceDef>;
  improvements: Table<ImprovementDef>;
  /** The improvement that is a road, or null: what lives in the map's road layer. */
  route: ImprovementDef | null;
  units: Table<UnitDef>;
  buildings: Table<BuildingDef>;
  civilizations: Table<CivilizationDef>;
  eras: Table<EraDef>;
  technologies: Table<TechnologyDef>;
  specialists: Table<SpecialistDef>;
  personalities: Table<PersonalityDef>;
  governments: Table<GovernmentDef>;
  traits: Table<TraitDef>;
  mapgen: MapgenPresets;
  rules: Rules;
}

/** Formats a Zod failure into something that names the file and the offending row. */
function fail(file: string, error: z.ZodError): never {
  const lines = error.issues
    .slice(0, 8)
    .map((i) => `  • ${i.path.join('.') || '(root)'}: ${i.message}`)
    .join('\n');
  throw new Error(`Invalid content in ${file}:\n${lines}`);
}

function parseOrFail<T>(file: string, schema: z.ZodType<T>, data: unknown): T {
  const result = schema.safeParse(data);
  if (!result.success) fail(file, result.error);
  return result.data;
}

let cached: Content | null = null;

/**
 * Load, validate and cross-check all game content.
 *
 * Cross-file checks matter as much as per-file ones: a feature that lists a terrain
 * type nobody declared is exactly the kind of bug that shows up as an empty map
 * three phases later.
 */
export function loadContent(): Content {
  if (cached) return cached;

  const terrain = buildTable(parseOrFail('terrain.json', z.array(TerrainDef).min(1), terrainData), 'terrain');
  const features = buildTable(parseOrFail('features.json', z.array(FeatureDef).min(1), featureData), 'feature');
  const resourceRows = parseOrFail('resources.json', z.array(ResourceDef).min(1), resourceData);
  // Technologies first: units, buildings, improvements and civilisations all name them.
  const eras = buildTable(parseOrFail('eras.json', z.array(EraDef).min(1), eraData), 'era');
  const technologies = buildTable(
    resolveTechnologies(parseOrFail('technologies.json', z.array(TechDef).min(1), technologyData), eras),
    'technology',
  );
  const techId = (label: string, owner: string, key: string | null): number => {
    if (key === null) return -1;
    const tech = technologies.byKey.get(key);
    if (!tech) throw new Error(`${label} "${owner}" names unknown technology "${key}"`);
    return tech.id;
  };

  const resources = buildTable(
    resourceRows.map((row) => ({ ...row, revealedId: techId('resource', row.key, row.revealedBy) })),
    'resource',
  );
  // A civilisation's resources are a bit mask (ADR-0063).
  if (resources.all.length > 31) throw new Error('resources.json: at most 31 resources (they are held as a bit mask)');

  const improvements = buildTable(
    parseOrFail('improvements.json', z.array(ImprovementDef).min(1), improvementData).map((row) => ({
      ...row,
      techId: techId('improvement', row.key, row.requiresTech),
    })),
    'improvement',
  );
  const units = buildTable(
    parseOrFail('units.json', z.array(UnitDef).min(1), unitData).map((row) => {
      const resolved = {
        ...row,
        techId: techId('unit', row.key, row.requiresTech),
        obsoleteTechId: techId('unit', row.key, row.obsoleteWith),
        upgradeId: -1,
        civId: -1,
        replacesId: -1,
        resourceIds: row.requiresResources.map((name) => {
          const resource = resources.byKey.get(name);
          if (!resource || resource.class !== 'strategic') throw new Error(`unit "${row.key}" requires unknown or non-strategic resource "${name}"`);
          return resource.id;
        }),
      };
      if ((row.domain === 'air') !== (row.airRange !== null)) throw new Error(`unit "${row.key}": aircraft, and only aircraft, have an airRange`);
      if (row.airCapacity > 0 && row.domain !== 'sea') throw new Error(`unit "${row.key}": only ships carry aircraft`);
      if (resolved.obsoleteTechId !== -1 && resolved.obsoleteTechId === resolved.techId) {
        throw new Error(`unit "${row.key}" is obsolete with the same technology that unlocks it`);
      }
      return resolved;
    }),
    'unit',
  );
  // Upgrade chains name other units, so they resolve once every unit has an id. A chain
  // must stay in one domain, always cost more, and never loop back on itself.
  for (const unit of units.all) {
    if (unit.upgradeTo === null) continue;
    const target = units.byKey.get(unit.upgradeTo);
    if (!target) throw new Error(`unit "${unit.key}" upgrades to unknown unit "${unit.upgradeTo}"`);
    if (target.domain !== unit.domain) throw new Error(`unit "${unit.key}" upgrades across domains to "${target.key}"`);
    if (target.cost <= unit.cost) throw new Error(`unit "${unit.key}" upgrades to "${target.key}", which is no dearer`);
    (unit as { upgradeId: number }).upgradeId = target.id;
  }
  const buildings = buildTable(
    parseOrFail('buildings.json', z.array(BuildingDef).min(1), buildingData).map((row) => ({
      ...row,
      techId: techId('building', row.key, row.requiresTech),
    })),
    'building',
  );
  const personalities = buildTable(
    parseOrFail('personalities.json', z.array(PersonalityDef).min(1), personalityData),
    'personality',
  );
  const governments = buildTable(
    parseOrFail('governments.json', z.array(GovernmentDef).min(2), governmentData).map((row) => ({
      ...row,
      techId: techId('government', row.key, row.requiresTech),
    })),
    'government',
  );
  for (const g of governments.all) {
    for (const who of Object.keys(g.aiValue)) {
      if (who !== 'base' && !personalities.byKey.has(who)) throw new Error(`government "${g.key}" values unknown personality "${who}"`);
    }
  }
  const traits = buildTable(parseOrFail('traits.json', z.array(TraitDef).min(1), traitData), 'trait');
  for (const trait of traits.all) {
    for (const key of Object.keys(trait.effects.buildingDiscount)) {
      if (!buildings.byKey.has(key)) throw new Error(`trait "${trait.key}" discounts unknown building "${key}"`);
    }
    for (const key of Object.keys(trait.effects.unitDiscount)) {
      if (!units.byKey.has(key)) throw new Error(`trait "${trait.key}" discounts unknown unit "${key}"`);
    }
  }
  const civilizations = buildTable(
    parseOrFail('civilizations.json', z.array(CivilizationDef).min(1), civilizationData).map((row) => {
      const startingTechIds = row.startingTechs.map((key) => techId('civilization', row.key, key));
      // A starting set must be closed: nobody begins knowing something without what it
      // is built on, or the tree screen would show a technology floating in mid-air.
      for (const id of startingTechIds) {
        for (const p of technologies.get(id).prerequisites) {
          if (!startingTechIds.includes(p)) {
            throw new Error(
              `civilization "${row.key}" starts with "${technologies.get(id).key}" but not its prerequisite "${technologies.get(p).key}"`,
            );
          }
        }
      }
      const personality = personalities.byKey.get(row.personality);
      if (!personality) throw new Error(`civilization "${row.key}" leans to unknown personality "${row.personality}"`);
      const traitIds = row.traits.map((t) => {
        const trait = traits.byKey.get(t);
        if (!trait) throw new Error(`civilization "${row.key}" has unknown trait "${t}"`);
        return trait.id;
      });
      if (traitIds[0] === traitIds[1]) throw new Error(`civilization "${row.key}" lists the same trait twice`);
      return { ...row, startingTechIds: [...startingTechIds].sort((a, b) => a - b), personalityId: personality.id, traitIds, uniqueUnitId: -1 };
    }),
    'civilization',
  );
  // Unique units (ADR-0062): one per civilisation at most, each standing in for a standard
  // unit of the same domain, needing the same technology and made obsolete by the same.
  for (const unit of units.all) {
    if (unit.civilization === null && unit.replaces === null) continue;
    if (unit.civilization === null || unit.replaces === null) throw new Error(`unit "${unit.key}" needs both civilization and replaces`);
    const civ = civilizations.byKey.get(unit.civilization);
    if (!civ) throw new Error(`unit "${unit.key}" belongs to unknown civilization "${unit.civilization}"`);
    const base = units.byKey.get(unit.replaces);
    if (!base || base.replaces !== null) throw new Error(`unit "${unit.key}" replaces unknown or unique unit "${unit.replaces}"`);
    if (base.domain !== unit.domain || base.techId !== unit.techId || base.obsoleteTechId !== unit.obsoleteTechId) {
      throw new Error(`unit "${unit.key}" must share its domain and technologies with "${base.key}"`);
    }
    if (civ.uniqueUnitId !== -1) throw new Error(`civilization "${civ.key}" has two unique units`);
    (civ as { uniqueUnitId: number }).uniqueUnitId = unit.id;
    (unit as { civId: number }).civId = civ.id;
    (unit as { replacesId: number }).replacesId = base.id;
  }
  const mapgen = parseOrFail('mapgen-presets.json', MapgenPresets, mapgenData);
  const specialists = buildTable(parseOrFail('specialists.json', z.array(SpecialistDef).min(1), specialistData), 'specialist');

  const rules = parseOrFail('rules.json', Rules, rulesData);
  techId('rules.resources', 'seaTech', rules.resources.seaTech);
  for (const key of [rules.government.start, rules.government.anarchy]) {
    if (!governments.byKey.has(key)) throw new Error(`rules.government names unknown government "${key}"`);
  }
  if (!governments.byKey.get(rules.government.start)!.selectable) throw new Error('the starting government must be selectable');

  // Zeroed typed arrays must mean something sensible, so index 0 is reserved.
  if (terrain.all[0]!.key !== 'ocean') {
    throw new Error('terrain.json must declare "ocean" first — a zeroed tile plane means terrain id 0.');
  }
  for (const [label, table] of [
    ['features', features],
    ['resources', resources],
    ['improvements', improvements],
  ] as const) {
    if (table.all[0]!.key !== 'none') {
      throw new Error(`${label}.json must declare "none" first — id 0 means "absent".`);
    }
  }

  // Cross-file referential integrity.
  const terrainKeys = new Set(terrain.all.map((t) => t.key));
  const checkTerrainRefs = (label: string, rows: readonly { key: string; validTerrain: string[] }[]) => {
    for (const row of rows) {
      for (const t of row.validTerrain) {
        if (!terrainKeys.has(t)) {
          throw new Error(`${label} "${row.key}" references unknown terrain "${t}"`);
        }
      }
    }
  };
  checkTerrainRefs('feature', features.all);
  checkTerrainRefs('resource', resources.all);
  checkTerrainRefs('improvement', improvements.all);

  for (const t of rules.vision.highGroundTerrain) {
    if (!terrainKeys.has(t)) {
      throw new Error(`rules.json vision.highGroundTerrain references unknown terrain "${t}"`);
    }
  }

  if (!mapgen.sizes.some((s) => s.key === mapgen.defaults.size)) {
    throw new Error(`mapgen defaults.size "${mapgen.defaults.size}" is not one of the declared sizes`);
  }

  // Typed-array planes are Uint8Array, so content tables cannot exceed 256 entries.
  for (const [label, table] of [
    ['terrain', terrain],
    ['features', features],
    ['resources', resources],
    ['improvements', improvements],
  ] as const) {
    if (table.all.length > 256) {
      throw new Error(`${label} has ${table.all.length} entries; tile planes are Uint8Array (max 256)`);
    }
  }

  // Saves remap technology ids through a Uint8Array like every other content table.
  if (technologies.all.length > 256) {
    throw new Error(`technologies has ${technologies.all.length} entries; saves remap through Uint8Array (max 256)`);
  }
  if (rules.research.startingRate > rules.research.maxRate) {
    throw new Error('rules.json research.startingRate is above research.maxRate');
  }
  if (rules.research.startingRate % rules.research.rateStep !== 0 || rules.research.maxRate % rules.research.rateStep !== 0) {
    throw new Error('rules.json research rates must be whole multiples of research.rateStep');
  }

  if (!specialists.byKey.has(rules.cities.autoSpecialist)) {
    throw new Error(`rules.json cities.autoSpecialist "${rules.cities.autoSpecialist}" is not a specialist`);
  }

  // A personality may only adjust weights that exist, or a typo would silently do nothing.
  for (const personality of personalities.all) {
    for (const weight of Object.keys(personality.weights)) {
      if (typeof (rules.ai as Record<string, unknown>)[weight] !== 'number') {
        throw new Error(`personality "${personality.key}" adjusts unknown rules.ai weight "${weight}"`);
      }
    }
  }

  cached = {
    terrain,
    features,
    resources,
    improvements,
    route: improvements.all.find((i) => i.route) ?? null,
    units,
    buildings,
    civilizations,
    eras,
    technologies,
    specialists,
    personalities,
    governments,
    traits,
    mapgen,
    rules,
  };
  return cached;
}

/**
 * Turn the technology file into a graph the engine can use, and refuse a bad one.
 *
 * Every check here is a bug that would otherwise surface as a technology nobody can
 * ever research — a prerequisite spelled wrongly, a loop, a classical technology that
 * needs a medieval one. Those are silent in play and loud here.
 */
function resolveTechnologies(
  rows: ReadonlyArray<z.infer<typeof TechDef>>,
  eras: Table<EraDef>,
): Array<Omit<TechnologyDef, 'id'>> {
  const index = new Map(rows.map((row, i) => [row.key, i]));

  const prerequisites = rows.map((row) => {
    const seen = new Set<string>();
    return row.requires.map((key) => {
      if (key === row.key) throw new Error(`technology "${row.key}" requires itself`);
      if (seen.has(key)) throw new Error(`technology "${row.key}" lists "${key}" twice`);
      seen.add(key);
      const id = index.get(key);
      if (id === undefined) throw new Error(`technology "${row.key}" requires unknown technology "${key}"`);
      return id;
    });
  });

  const eraIndex = rows.map((row) => {
    const era = eras.byKey.get(row.era);
    if (!era) throw new Error(`technology "${row.key}" is in unknown era "${row.era}"`);
    return era.id;
  });

  // Depth by memoised search, with a visiting set so a cycle is reported rather than
  // recursing until the stack gives out.
  const depth = new Array<number>(rows.length).fill(-1);
  const visiting = new Set<number>();
  const measure = (id: number): number => {
    if (depth[id]! >= 0) return depth[id]!;
    if (visiting.has(id)) throw new Error(`technology "${rows[id]!.key}" is part of a prerequisite loop`);
    visiting.add(id);
    let deepest = -1;
    for (const p of prerequisites[id]!) deepest = Math.max(deepest, measure(p));
    visiting.delete(id);
    depth[id] = deepest + 1;
    return depth[id]!;
  };
  rows.forEach((_, id) => measure(id));

  rows.forEach((row, id) => {
    for (const p of prerequisites[id]!) {
      if (eraIndex[p]! > eraIndex[id]!) {
        throw new Error(`technology "${row.key}" (${row.era}) requires "${rows[p]!.key}" from a later era`);
      }
    }
  });

  return rows.map((row, id) => ({
    ...row,
    eraIndex: eraIndex[id]!,
    prerequisites: prerequisites[id]!,
    depth: depth[id]!,
  }));
}

/** Test-only: drop the cache so a test can reload content. */
export function resetContentCache(): void {
  cached = null;
}
